import * as React from "react"
import Helmet from "react-helmet"

import { formatDistanceToNow } from "date-fns"

import PageContainer from "@podcastsoundboard/ps-lib/components/PageContainer"

import searchBlogPosts from "../../../../api/blogposts/search"
import AppBar from "../../../../components/AppBar"
import Footer from "../../../../components/Footer"
import FullPageContentContainer from "../../../../components/FullPageContentContainer"
import contentToReadTimeString from "../../../../helpers/strings/contentToReadTimeString"
import { useAppSelector } from "../../../../redux"
import { BlogPost } from "../../../../types/BlogPost"
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"

export default function PostList() {
  const [errorMessage, setErrorMessage] = React.useState("")
  const [blogPostsLoading, setBlogPostsLoading] = React.useState(false)
  const [blogPosts, setBlogPosts] = React.useState<BlogPost[] | null>(null)

  React.useEffect(() => {
    searchBlogPosts({ hidden: false })
      .then((response) => {
        setBlogPostsLoading(false)
        setBlogPosts(response.blogPosts)
      })
      .catch((err) => {
        console.error(err)
        setBlogPostsLoading(false)
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }, [])
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)
  return (
    <PageContainer darkModeEnabled={darkModeEnabled} className="min-h-screen">
      <AppBar title="Podcast Soundboard" />

      <Toolbar />

      <Helmet>
        <title>Guides & Blog</title>
        <meta
          name="description"
          content="Helpful information about how to set up Podcast Soundboard and route audio between applications."
        />
      </Helmet>

      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert className="rounded-none" severity="error">
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <FullPageContentContainer maxWidth="md">
        {blogPostsLoading && (
          <Box className="flex justify-center">
            <CircularProgress />
          </Box>
        )}

        <Typography variant="h4" color="text.primary" className="mt-4 mb-10">
          Guides
        </Typography>

        {blogPosts &&
          blogPosts.map((post) => (
            <Link
              href={`/app/blog/${post.uuid}`}
              className="no-underline"
              key={post.uuid}
            >
              <Paper
                elevation={4}
                style={{ cursor: "pointer" }}
                className="p-6 my-6"
              >
                <Box className="blog-post-item">
                  {post.coverImageUrl && (
                    <Box className="blog-post-image">
                      <img
                        alt="Cover art"
                        style={{ width: "100%" }}
                        src={post.coverImageUrl}
                      />
                    </Box>
                  )}
                  <Box>
                    <Typography className="mb-4 blog-title" variant="h4">
                      {post.title}
                    </Typography>
                    <Typography
                      className="mb-4 blog-subtitle"
                      variant="body1"
                      component="p"
                    >
                      {post.subtitle}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body1"
                      component="p"
                      className="blog-subtitle"
                    >
                      Created{" "}
                      {formatDistanceToNow(new Date(post.createdAt), {
                        addSuffix: true,
                      })}{" "}
                      | {contentToReadTimeString(post.content)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Link>
          ))}
      </FullPageContentContainer>

      <Footer />
    </PageContainer>
  )
}
